import type {
  DocumentMetadataUserEditValues,
  DocumentPageMetadataUserEditSource,
  DocumentPageMetadataUserEditStatus,
  DocumentPageMetadataUserEditSurface,
  DocumentPageMetadataUserEditValues,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import {
  buildDocumentMetadataUserEditPut,
  type DocumentMetadataUserEditPutData,
} from 'features/knowledgeDocumentViewer/utils/buildDocumentMetadataUserEditPut'
import {
  buildDocumentPageMetadataUserEditPut,
  type DocumentPageMetadataUserEditPutData,
  resolvePageMetadataPipelineConfigId,
} from 'features/knowledgeDocumentViewer/utils/buildDocumentPageMetadataUserEditPut'
import {
  buildFamilyMemberDocumentMetadataUserValues,
  type FamilyDocumentMetadataSaveMember,
} from 'features/knowledgeDocumentViewer/utils/buildFamilyDocumentMetadataUserEditPuts'
import {
  buildFamilyMemberPageMetadataUserValues,
  type FamilyPageMetadataSaveMember,
} from 'features/knowledgeDocumentViewer/utils/buildFamilyPageMetadataUserEditPuts'
import {
  mergeDocumentMetadataValues,
  mergePageMetadataValues,
} from 'features/knowledgeDocumentViewer/utils/effectiveMetadata'
import {
  allowsCrossSurfaceMetadataCascade,
  buildDocumentValuesCascadedFromPageEdit,
  buildPageValuesCascadedFromDocumentEdit,
} from 'features/knowledgeDocumentViewer/utils/sharedMetadataFieldBridge'
import type { RolodexEntryPutJson } from 'types'

export type FamilyPageMetadataCascadeMember = FamilyPageMetadataSaveMember

export type FamilyDocumentMetadataCascadeMember =
  FamilyDocumentMetadataSaveMember

function uniqueByBqDocumentId<T extends { bqDocumentId: string }>(
  members: readonly T[]
): T[] {
  const byId = new Map<string, T>()
  for (const member of members) {
    if (!byId.has(member.bqDocumentId)) {
      byId.set(member.bqDocumentId, member)
    }
  }
  return [...byId.values()]
}

/** Document puts for shared fields changed in a page-family edit. */
export function buildDocumentCascadePutsFromPageFamilyEdit(args: {
  siteId: string
  organizationId: string
  editedBy: string
  editedAt: string
  sharedPageUserValues: DocumentPageMetadataUserEditValues
  members: readonly FamilyPageMetadataCascadeMember[]
}): RolodexEntryPutJson<DocumentMetadataUserEditPutData>[] {
  const puts: RolodexEntryPutJson<DocumentMetadataUserEditPutData>[] = []
  for (const member of uniqueByBqDocumentId(args.members)) {
    if (member.systemDocument === undefined) continue
    if (
      !allowsCrossSurfaceMetadataCascade(
        member.systemDocument.entry.data.total_pages
      )
    ) {
      continue
    }
    const pageBaseline = mergePageMetadataValues(
      member.systemPage?.entry.data,
      member.priorEdit?.entry.data.values
    )
    const pageNext = buildFamilyMemberPageMetadataUserValues(
      args.sharedPageUserValues,
      member.systemPage,
      member.priorEdit
    )
    const documentBaseline = mergeDocumentMetadataValues(
      member.systemDocument.entry.data,
      member.documentPriorEdit?.entry.data.values
    )
    const cascaded = buildDocumentValuesCascadedFromPageEdit({
      pageBaseline,
      pageNext,
      documentBaseline,
      requireAlignment: false,
    })
    if (cascaded === undefined) continue
    puts.push(
      buildDocumentMetadataUserEditPut({
        siteId: args.siteId,
        organizationId: args.organizationId,
        bqDocumentId: member.bqDocumentId,
        editedBy: args.editedBy,
        editedAt: args.editedAt,
        systemDocument: member.systemDocument,
        priorEdit: member.documentPriorEdit,
        userValues: cascaded,
      })
    )
  }
  return puts
}

/** Page puts for shared fields changed in a document-family edit. */
export function buildPageCascadePutsFromDocumentFamilyEdit(args: {
  siteId: string
  organizationId: string
  status: DocumentPageMetadataUserEditStatus
  source: DocumentPageMetadataUserEditSource
  surface: DocumentPageMetadataUserEditSurface
  pipelineConfigIdFallback: string | undefined
  sharedDocumentUserValues: DocumentMetadataUserEditValues
  members: readonly FamilyDocumentMetadataCascadeMember[]
}): RolodexEntryPutJson<DocumentPageMetadataUserEditPutData>[] {
  const puts: RolodexEntryPutJson<DocumentPageMetadataUserEditPutData>[] = []
  for (const member of uniqueByBqDocumentId(args.members)) {
    if (member.pageNum === undefined) continue
    if (member.systemPage === undefined) continue
    if (
      !allowsCrossSurfaceMetadataCascade(
        member.systemDocument?.entry.data.total_pages
      )
    ) {
      continue
    }
    const documentBaseline = mergeDocumentMetadataValues(
      member.systemDocument?.entry.data,
      member.priorEdit?.entry.data.values
    )
    const documentNext = buildFamilyMemberDocumentMetadataUserValues(
      args.sharedDocumentUserValues,
      member.systemDocument,
      member.priorEdit
    )
    const pageBaseline = mergePageMetadataValues(
      member.systemPage.entry.data,
      member.pagePriorEdit?.entry.data.values
    )
    const cascaded = buildPageValuesCascadedFromDocumentEdit({
      documentBaseline,
      documentNext,
      pageBaseline,
      requireAlignment: false,
    })
    if (cascaded === undefined) continue
    puts.push(
      buildDocumentPageMetadataUserEditPut({
        siteId: args.siteId,
        organizationId: args.organizationId,
        bqDocumentId: member.bqDocumentId,
        pageNum: member.pageNum,
        systemPage: member.systemPage,
        priorEdit: member.pagePriorEdit,
        userValues: cascaded,
        status: args.status,
        source: args.source,
        surface: args.surface,
        pipelineConfigId: resolvePageMetadataPipelineConfigId(
          member.systemPage,
          args.pipelineConfigIdFallback
        ),
      })
    )
  }
  return puts
}
